import React, { useState, useEffect } from 'react';
import { Printer, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../../api/config';

const TakeawayPrintBill = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userToken: token } = useAuth();
  const [bill, setBill] = useState(null);
  const [restaurantInfo, setRestaurantInfo] = useState({
    restaurantName: '',
    location: '',
    phoneNumber: '',
    gstin: '',
    fssaiNumber: '',
    note: 'Thank you, Visit Again!'
  });
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  const handleBack = () => {
    navigate('/dashboard');
  };

  useEffect(() => {
    if (!billId) {
      alert('Error: Bill ID is required');
      setLoading(false);
      return;
    }

    const fetchBillData = async () => {
      try {
        if (location.state?.billData) {
          console.log('Using bill data from navigation state:', location.state.billData);
          setBill(location.state.billData);
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/bills/${billId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch bill data');
        }

        const responseData = await response.json();
        const billData = responseData.bill || responseData.data || responseData;
        
        if (!billData) {
          throw new Error('Invalid bill data received from server');
        }

        if (!billData.items || !Array.isArray(billData.items)) {
          throw new Error('Bill items data is missing or invalid');
        }

        const normalizedBill = {
          ...billData,
          items: billData.items.map(item => ({
            ...item,
            itemName: item.itemName || item.item_name || item.name || 'Unknown Item'
          }))
        };

        setBill(normalizedBill);
      } catch (error) {
        console.error('Error fetching bill:', error);
        alert(error.message || 'Failed to fetch bill data');
      } finally {
        setLoading(false);
      }
    };

    const fetchRestaurantInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const responseData = await response.json();
          const data = responseData.data || responseData;
          setRestaurantInfo({
            restaurantName: data.restaurantName || '',
            location: data.location || '',
            phoneNumber: data.phoneNumber || '',
            gstin: data.gstin || '',
            fssaiNumber: data.fssaiNumber || '',
            note: data.note || 'Thank you, Visit Again!'
          });
        }
      } catch (error) {
        console.error('Error fetching restaurant info:', error);
      }
    };

    fetchBillData();
    fetchRestaurantInfo();
  }, [billId, token, location.state]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handlePrint = () => {
    setPrinting(true);
    window.print();
    setPrinting(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-base text-black">Loading bill data...</p>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 p-4">
        <AlertCircle size={48} className="text-red-500" />
        <p className="mt-4 text-lg text-black mb-6">Bill not found</p>
        <button
          className="py-2 px-4 bg-gray-600 text-white rounded-lg"
          onClick={handleBack}
        >
          <div className="flex items-center">
            <ArrowLeft size={16} className="mr-2" />
            <span>Go Back</span>
          </div>
        </button>
      </div>
    );
  }

  if (!bill.items || !Array.isArray(bill.items)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 p-4">
        <AlertCircle size={48} className="text-red-500" />
        <p className="mt-4 text-lg text-black mb-6">Invalid bill data: No items found</p>
        <button
          className="py-2 px-4 bg-gray-600 text-white rounded-lg"
          onClick={handleBack}
        >
          <div className="flex items-center">
            <ArrowLeft size={16} className="mr-2" />
            <span>Go Back</span>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Print-only styles */}
      <style type="text/css" media="print">
        {`
          @page { size: 80mm auto; margin: 0; }
          .no-print { display: none !important; }
          .print-receipt { width: 100%; max-width: 100%; }
          body { width: 80mm; margin: 0; padding: 0; }
          nav, header, .hamburger, aside, .sidebar { display: none !important; }
          p, span, div, td, th { color: black !important; font-weight: 600 !important; }
          .print-bold { font-weight: 800 !important; }
        `}
      </style>
      
      <div className="print-container">
        <div className="container mx-auto max-w-2xl px-4 py-6 mb-20 print-receipt">
          {/* RESTAURANT HEADER */}
          <div className="text-center border-b-2 border-black py-6">
            <h2 className="text-3xl font-bold text-black mb-2 print-bold">{restaurantInfo.restaurantName}</h2>
            {restaurantInfo.location && <p className="text-lg text-black mb-1">{restaurantInfo.location}</p>}
            {restaurantInfo.phoneNumber && <p className="text-lg text-black">Phone: {restaurantInfo.phoneNumber}</p>}
            {restaurantInfo.gstin && <p className="text-lg text-black">GSTIN: {restaurantInfo.gstin}</p>}
            {restaurantInfo.fssaiNumber && <p className="text-lg text-black">FSSAI: {restaurantInfo.fssaiNumber}</p>}
          </div>

          <div className="p-4">
            {/* TAKEAWAY INDICATOR */}
            <div className="mb-6 text-center">
              <div className="inline-block bg-orange-500 text-white px-6 py-3 rounded-lg">
                <p className="text-2xl font-bold print-bold">TAKEAWAY ORDER</p>
              </div>
            </div>

            {/* BILL INFO */}
            <div className="flex flex-wrap mb-6 border-b-2 border-black pb-4">
              <div className="w-1/2">
                <p className="text-2xl text-black mb-2"><span className="text-xl font-bold print-bold">Bill #:</span> {bill.billNumber}</p>
                <p className="text-2xl text-black mb-2"><span className="text-xl font-bold print-bold">Date:</span> {formatDate(bill.createdAt)}</p>
              </div>
              <div className="w-1/2">
                <p className="text-xl text-black mb-2">
                  <span className="text-xl font-bold print-bold">Payment:</span> {bill.paymentMethod || bill.paymentMode || 'CASH'}
                </p>
                {bill.names && <p className="text-xl text-black"><span className="text-xl font-bold print-bold">Name:</span> {bill.names}</p>}
                {bill.mobileNumber && <p className="text-xl text-black"><span className="text-xl font-bold print-bold">Mobile:</span> {bill.mobileNumber}</p>}
              </div>
            </div>

            {/* ITEMS TABLE */}
            <div className="mb-6 overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2 px-1 font-bold text-lg text-black print-bold">Item</th>
                    <th className="text-right py-2 px-1 font-bold text-lg text-black print-bold">Price</th>
                    <th className="text-right py-2 px-1 font-bold text-lg text-black print-bold">Qty</th>
                    <th className="text-right py-2 px-1 font-bold text-lg text-black print-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.items.map((item, index) => {
                    const itemName = item.itemName || item.item_name || item.name || 'Unknown Item';
                    const price = item.variant && item.variant.price ? item.variant.price : item.price;
                    const quantity = item.quantity || 1;
                    const itemTotal = price * quantity;
                    
                    const variantName = item.variant?.name || '';
                    const cleanVariantName = variantName
                      .replace(/\(Custom\)/gi, '')
                      .replace(/^Custom$/gi, '')
                      .trim();
                    
                    return (
                      <tr key={index} className="border-b border-gray-400">
                        <td className="py-2 px-1 text-lg text-black">
                          <div>{itemName}</div>
                          {cleanVariantName && (
                            <div className="text-base text-black">{cleanVariantName}</div>
                          )}
                          {item.note && (
                            <div className="text-sm text-gray-700 italic">Note: {item.note}</div>
                          )}
                        </td>
                        <td className="py-2 px-1 text-lg text-black text-right">₹{price.toFixed(2)}</td>
                        <td className="py-2 px-1 text-lg text-black text-right">{quantity}</td>
                        <td className="py-2 px-1 text-lg text-black text-right">₹{itemTotal.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* SUMMARY SECTION */}
            <div className="mb-6 border-t-2 border-black pt-4">
              <div className="flex justify-between mb-2">
                <span className="text-lg text-black">Subtotal</span>
                <span className="text-lg text-black">₹{(bill.subtotal || 0).toFixed(2)}</span>
              </div>
              
              {(bill.cgst > 0 || bill.sgst > 0) && (
                <>
                  <div className="flex justify-between mb-2">
                    <span className="text-lg text-black">CGST (2.5%)</span>
                    <span className="text-lg text-black">₹{(bill.cgst || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-lg text-black">SGST (2.5%)</span>
                    <span className="text-lg text-black">₹{(bill.sgst || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
              
              {((bill.discountPercentage > 0) || (bill.discountAmount > 0)) && (
                <div className="flex justify-between mb-2">
                  <span className="text-lg text-black">
                    Discount {bill.discountType === 'percentage' 
                      ? `(${bill.discountPercentage}%)` 
                      : '(Amount)'}
                  </span>
                  <span className="text-lg text-black">-₹{(bill.discountAmount || 0).toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between pt-4 mt-4 border-t-2 border-black">
                <span className="text-xl font-bold text-black print-bold">TOTAL</span>
                <span className="text-xl font-bold text-black print-bold">₹{(bill.totalAmount || 0).toFixed(2)}</span>
              </div>
            </div>

            {/* NOTE SECTION */}
            <div className="text-center my-6 py-4 border-t border-b border-gray-400">
              <p className="text-2xl font-medium text-black">{restaurantInfo.note}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Button Container */}
      <div className="sticky bottom-0 left-0 right-0 border-t border-black p-4 z-10 no-print bg-white">
        <div className="container mx-auto max-w-2xl flex justify-between">
          <button
            className="w-1/3 bg-gray-600 hover:bg-gray-700 text-white py-3 px-6 rounded-lg mr-4 flex items-center justify-center transition-colors"
            onClick={handleBack}
          >
            <ArrowLeft size={18} className="mr-2" />
            <span>Back</span>
          </button>
            
          <button
            className="w-2/3 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg flex items-center justify-center transition-colors"
            onClick={handlePrint}
            disabled={printing}
          >
            {printing ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            ) : (
              <>
                <Printer size={20} className="mr-2" />
                <span>Print Bill</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TakeawayPrintBill;